import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

export interface OwnedRepoComponent {
  /** Stringified Backstage entity reference, e.g. `Component:default/my-service`. */
  entityRef: string;
  title: string;
  /** `owner/repo` GitHub slug from the `github.com/project-slug` annotation. */
  slug: string;
}

export interface UseOwnedRepoComponentsResult {
  components: OwnedRepoComponent[];
  loading: boolean;
  error: string | null;
}

/**
 * Owned `Component` entities that have a `github.com/project-slug`
 * annotation. Used to let a user pick which repository an autofix should
 * target when the failing entity itself has no repo association (e.g. a
 * cloud `Resource`). Pass `enabled: false` to skip the catalog fetch when the
 * picker isn't needed.
 */
export const useOwnedRepoComponents = (
  enabled: boolean = true,
): UseOwnedRepoComponentsResult => {
  const [components, setComponents] = useState<OwnedRepoComponent[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const identity = await identityApi.getBackstageIdentity();
        const response = await catalogApi.getEntities({
          filter: {
            kind: ['Component'],
            'relations.ownedBy': identity.ownershipEntityRefs ?? [],
          },
        });

        const owned: OwnedRepoComponent[] = [];
        for (const entity of response.items ?? []) {
          const slug = entity.metadata.annotations?.['github.com/project-slug'];
          if (slug) {
            owned.push({
              entityRef: `${entity.kind}:${
                entity.metadata.namespace ?? 'default'
              }/${entity.metadata.name}`,
              title: entity.metadata.title ?? entity.metadata.name,
              slug,
            });
          }
        }

        if (!cancelled) {
          setComponents(owned);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to load owned components',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [catalogApi, identityApi, enabled]);

  return { components, loading, error };
};
